import { designAssetIds, parseDesignerDocument, type DesignerDocument } from '@pz/scene';
import type { DesignerCtx } from './panels.js';
import { run } from './commands.js';

/**
 * The design as a file: what "Preuzmi dizajn", "Učitaj dizajn" and the
 * clipboard all move around.
 *
 * A document carries the answer and the zone size it was made for, never the
 * pictures: an asset id is a reference to something the shop owns, and a file
 * that embedded a shopper's photograph would be a privacy problem handed
 * around by e-mail. Loading therefore refuses when an id in the file is not
 * registered with this renderer, and says why.
 */

export function designDocument(ctx: DesignerCtx, selectedOnly = false): DesignerDocument {
  const answer = structuredClone(ctx.store.value());
  if (selectedOnly) {
    const ids = new Set(ctx.selection.size
      ? [...ctx.selection]
      : ctx.selected() ? [ctx.selected()!.id] : []);
    // A grouped layer never travels alone: copying half a group and pasting it
    // would leave a groupId pointing at nothing.
    const groups = new Set(answer.layers.filter((layer) => ids.has(layer.id)).map((layer) => layer.groupId).filter(Boolean));
    answer.layers = answer.layers.filter((layer) => ids.has(layer.id) || (layer.groupId && groups.has(layer.groupId)));
    delete answer.background;
    delete answer.sticker;
  }
  return { format: 'personalizy-design', version: 1, sizeMm: { ...ctx.config.sizeMm }, answer };
}

export function applyDocument(
  ctx: DesignerCtx,
  raw: unknown,
  mode: 'replace' | 'append',
  scale: 'fit' | 'physical' = 'fit',
): boolean {
  try {
    const document = parseDesignerDocument(raw);
    const missing = designAssetIds(document.answer).filter((id) => !ctx.renderer.assetUrl(id));
    if (missing.length) throw new Error(ctx.texts.fileMissingAssets);
    return run(ctx, { op: 'composition', document, mode, scale });
  } catch (error) {
    ctx.toast(error instanceof Error ? error.message : ctx.texts.fileBroken, 'error');
    return false;
  }
}
